import {objectFromEntries, entry as castEntry} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {NOT_ARCHIVED_PURPOSE_STRING} from 'archive/ArchiveConstants';
import * as ArchiveFilterUtil from 'archive/ArchiveFilterUtil';
import ArchivePurposeDropdown from 'archive/components/ArchivePurposeDropdown';
import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import {BENCHLING_APPS_WEB_URL} from 'benchling-apps/BenchlingAppDAO';
import BenchlingConfig from 'BenchlingConfig';
import navigateToURL from 'lib/navigateToURL';
import Tooltip from 'ui-library/bs/Tooltip';
import {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_SEMIDARK} from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import EditableItemTable, {EditableItemRowProps} from 'ui-library/EditableItemTable';
import legacyRunModal from 'ui-library/legacyRunModal';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import {readableTime} from 'util/DateUtil';

import {getBenchlingAppPageUrl, onAppsArchive, onAppsUnarchive} from '../lib';
import {AppInstallation, AppDefinition} from './BenchlingAppsPage';
import CreateAppModal from './create-app/CreateAppModal';
import CreateBenchlingAppFromManifestModalLegacy from './create-app/CreateBenchlingAppFromManifestModalLegacy';

export interface BenchlingAppListingProps {
  appInstallations: Array<AppInstallation> | null;
  appDefinitions: Array<AppDefinition> | null;
  forceRefetchApps: () => void;
}

interface BenchlingAppRow {
  name: string;
  version: string;
  lastModified: string;
  audience?: 'Public' | 'Private';
  distribution?: 'In-Development' | 'Shared';
}

interface AppMetaInfo {
  id: string;
  createdAt: string;
  installation: AppInstallation | null;
  definition: AppDefinition | null;
}

export default function BenchlingAppListing(props: BenchlingAppListingProps): JSX.Element {
  const isLoading = props.appInstallations == null || props.appDefinitions == null;
  const [archivePurposeFilters, setArchivePurposeFilters] = useState([NOT_ARCHIVED_PURPOSE_STRING]);
  const filteredAppInstallations = getFilteredAppInstallations(props.appInstallations);
  const filteredAppDefinitions = getFilteredAppDefinitions(props.appDefinitions || []);
  const rows = getRows();

  function getAppInfos(apps: Array<AppDefinition> | Array<AppInstallation>): Array<AppMetaInfo> {
    return apps.map((app) => {
      const isAppDefinition = app.__typename === 'BenchlingAppDefinitionNode';
      return {
        id: app.id,
        createdAt: app.createdAt,
        installation: isAppDefinition ? null : app,
        definition: isAppDefinition ? app : null,
      };
    });
  }

  function getRows(): Array<EditableItemRowProps> {
    if (isLoading) {
      return [];
    }

    // Filter out any app installation with an app definition ID. We only want legacy apps; all other
    // global apps should be represented in the appDefinitions prop.
    const legacyAppInstallations = filteredAppInstallations.filter(
      (appInstallation) => appInstallation.appDefinitionId == null
    );
    const appDefinitionInfos: Array<AppMetaInfo> = getAppInfos(filteredAppDefinitions);
    const legacyAppInfos: Array<AppMetaInfo> = getAppInfos(legacyAppInstallations);

    // Concatenate the list of legacy apps to the list of app definitions, and sort them all by createdAt
    const sortedApps = appDefinitionInfos.concat(legacyAppInfos);
    sortedApps.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    const appIndexById = objectFromEntries(sortedApps.map((a, i) => castEntry(a.id, i)));

    const rows: Array<EditableItemRowProps> = [];
    for (const appInfo of sortedApps) {
      const isLegacyApp = appInfo.installation != null;
      const rowInfo: BenchlingAppRow = isLegacyApp
        ? rowForAppInstallation(appInfo.installation!)
        : rowForAppDefinition(appInfo.definition!);
      const archived = Boolean(
        isLegacyApp
          ? appInfo.installation && isArchived(appInfo.installation)
          : !BenchlingConfig.ENABLE_BENCHLING_APPS_CREATE_INSTALL_SEPARATION &&
              appInfo.definition!.installation &&
              isArchived(appInfo.definition!.installation)
      );
      const row: EditableItemRowProps = {
        id: appInfo.id,
        isArchived: archived,
        isArchivable: isLegacyApp,
        itemIndex: appIndexById[appInfo.id],
        values: renderRowForApp(rowInfo),
      };
      rows.push(row);
    }
    return rows;
  }

  function renderForLegacyApp(appProperty: string): JSX.Element {
    const message = `This app was built using legacy apps. ${appProperty} is not supported.`;
    return (
      <Tooltip container="body" placement={TooltipPlacement.TOP} title={message}>
        <span className={css(styles.legacyColumnValue)}>None</span>
      </Tooltip>
    );
  }

  function renderRowForApp(row: BenchlingAppRow): Array<JSX.Element> {
    return [
      <span>{row.name}</span>,
      <span>{row.version}</span>,
      <span>{readableTime(row.lastModified, {showTime: false})}</span>,
      row.audience ? <span>{row.audience}</span> : renderForLegacyApp('Audience'),
      row.distribution ? <span>{row.distribution}</span> : renderForLegacyApp('Distribution'),
    ];
  }

  function rowForAppInstallation(appInstallation: AppInstallation): BenchlingAppRow {
    // If not a global app, then return the installed app version.
    return {
      name: appInstallation.name,
      version: appInstallation.versionNumber!,
      lastModified: appInstallation.modifiedAt,
    };
  }

  function rowForAppDefinition(appDefinition: AppDefinition): BenchlingAppRow {
    // If a global app, returns the most recent version of the app in the most
    // public publicationState
    const version = appDefinition.latestReleaseVersion!;
    return {
      name: version.name,
      version: version.versionId,
      lastModified: version.modifiedAt!,
      audience: appDefinition.audience === 'PUBLIC' ? 'Public' : 'Private',
      distribution: version.publicationState === 'PRIVATE' ? 'In-Development' : 'Shared',
    };
  }

  function handleChangeArchivePurposeFilters(selectedArchivePurposeFilters: Array<string>): void {
    setArchivePurposeFilters(selectedArchivePurposeFilters);
  }

  function getFilteredAppInstallations(
    appInstallations: Array<AppInstallation> | null
  ): Array<AppInstallation> {
    return ArchiveFilterUtil.filterItemsToPurposes(appInstallations, archivePurposeFilters) || [];
  }

  function getFilteredAppDefinitions(appDefinitions: Array<AppDefinition>): Array<AppDefinition> {
    const filteredAppDefs: Array<AppDefinition> = [];
    for (const appDef of appDefinitions) {
      if (appDef.installation == null || BenchlingConfig.ENABLE_BENCHLING_APPS_CREATE_INSTALL_SEPARATION) {
        filteredAppDefs.push(appDef);
      } else {
        const filteredInstallation =
          ArchiveFilterUtil.filterItemsToPurposes([appDef.installation], archivePurposeFilters) || [];
        if (filteredInstallation.length > 0) {
          filteredAppDefs.push(appDef);
        }
      }
    }
    return filteredAppDefs;
  }

  function _onAppArchive(i: number): void {
    if (isLoading) {
      return;
    }

    const row: EditableItemRowProps = rows[i];
    const appInstallation = props.appInstallations!.find((a) => a.id === row.id);
    assert(appInstallation !== undefined, `Expected app installation with id ${row.id}`);
    onAppsArchive([appInstallation], props.forceRefetchApps);
  }

  async function _onAppUnarchive(i: number): Promise<void> {
    if (isLoading) {
      return;
    }

    const row: EditableItemRowProps = rows[i];
    const appInstallation = props.appInstallations!.find((a) => a.id === row.id);
    assert(appInstallation !== undefined, `Expected app installation with id ${row.id}`);
    await onAppsUnarchive([appInstallation], props.forceRefetchApps);
  }

  function createAppButton(): JSX.Element {
    // TODO (BNCH-92479): remove this conditional logic when we migrate all legacy apps
    const useNewAppDefinitionUI =
      BenchlingConfig.ENABLE_BENCHLING_APPS_GLOBAL_DEFINITIONS ||
      BenchlingConfig.ENABLE_BENCHLING_APPS_DEFINITION_SQLA;
    return useNewAppDefinitionUI ? (
      <Button
        buttonStyle={ButtonStyle.SECONDARY}
        size={ButtonSize.SMALL}
        text="Create app"
        dataTestAttrs={{component: 'BenchlingAppListing', element: 'openCreateAppModalButton'}}
        onClick={() => {
          legacyRunModal(CreateAppModal, {});
        }}
      />
    ) : (
      <Dropdown
        menu={
          <Menu width={MenuWidth.SMALL}>
            <Option
              onSelect={() => {
                navigateToURL(`${BENCHLING_APPS_WEB_URL}/create`);
              }}
              dataTestAttrs={{
                component: 'BenchlingAppListing',
                element: 'createDropdown',
                key: 'fromScratch',
              }}
            >
              From scratch
            </Option>
            <Option onSelect={() => legacyRunModal(CreateBenchlingAppFromManifestModalLegacy, {})}>
              From manifest
            </Option>
          </Menu>
        }
      >
        <Button
          buttonStyle={ButtonStyle.SECONDARY}
          size={ButtonSize.SMALL}
          text="Create app"
          dataTestAttrs={{component: 'BenchlingAppListing', element: 'createDropdownButton'}}
        />
      </Dropdown>
    );
  }

  const installationsList = props.appInstallations || [];
  return (
    <EditableItemTable
      tableName="Apps"
      columnHeaders={[
        {name: 'Name', width: 3},
        {name: 'Version', width: 2},
        {name: 'Last Modified', width: 2},
        {name: 'Audience', width: 2},
        {name: 'Distribution', width: 3},
      ]}
      emptyMessage="No apps are present"
      isLoading={isLoading}
      writable={true}
      getEditUrl={(i: number) => getBenchlingAppPageUrl(isLoading ? null : rows[i].id || null)}
      rows={rows}
      onArchive={_onAppArchive}
      onUnarchive={_onAppUnarchive}
      customCreateButton={createAppButton()}
      leftSubheaderNode={
        <div className="u-flex-display">
          <ArchivePurposeDropdown
            itemIds={isLoading ? [] : installationsList.map((ba) => ba.id)}
            onChangePurposes={handleChangeArchivePurposeFilters}
            selectedPurposes={archivePurposeFilters}
          />
        </div>
      }
    />
  );
}

const styles = StyleSheet.create({
  legacyColumnValue: {
    color: GRAY_SEMIDARK,
  },
});
